import React from "react";
import { useNavigate } from "react-router";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { useExternalRegisterImport } from "../import/externalRegisterImportProvider";
import { EXTERNAL_REGISTER_SETTINGS_ROUTES } from "../externalRegisterRoutes";

export function ExternalRegisterImportHistoryPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { historySummaries, dismissHistory, draftIsActive } = useExternalRegisterImport();

  return (
    <Box sx={{ height: "100%", width: "100%", overflow: "auto", p: 3 }}>
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={2}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>
                  {t("externalRegister.history.title")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("externalRegister.history.description")}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} alignItems="center">
                {draftIsActive && (
                  <Typography variant="body2" color="text.secondary">
                    {t("externalRegister.history.activeDraft")}
                  </Typography>
                )}
                <Button
                  onClick={() => navigate(EXTERNAL_REGISTER_SETTINGS_ROUTES.import)}
                  variant="outlined"
                  size="small"
                >
                  {t("externalRegister.history.buttons.backToImport")}
                </Button>
              </Stack>
            </Stack>

            <Alert severity="info" variant="outlined">
              {t("externalRegister.history.alert")}
            </Alert>

            {historySummaries.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t("externalRegister.history.empty")}
              </Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("externalRegister.history.table.headers.completed")}</TableCell>
                      <TableCell>{t("externalRegister.history.table.headers.status")}</TableCell>
                      <TableCell>{t("externalRegister.history.table.headers.imported")}</TableCell>
                      <TableCell>{t("externalRegister.history.table.headers.errors")}</TableCell>
                      <TableCell>{t("externalRegister.history.table.headers.newPatients")}</TableCell>
                      <TableCell>{t("externalRegister.history.table.headers.updates")}</TableCell>
                      <TableCell>{t("externalRegister.history.table.headers.confidence")}</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {historySummaries.map((summary) => (
                      <TableRow key={summary.id}>
                        <TableCell>{new Date(summary.completedAt).toLocaleString()}</TableCell>
                        <TableCell>{summary.status}</TableCell>
                        <TableCell>{summary.successfullyImported} / {summary.totalRecords}</TableCell>
                        <TableCell>{summary.errors}</TableCell>
                        <TableCell>{summary.newPatientsCreated}</TableCell>
                        <TableCell>{summary.updatedExistingEntries}</TableCell>
                        <TableCell>
                          {t("externalRegister.history.table.confidenceSummary", {
                            high: summary.confidenceCounts.HIGH,
                            medium: summary.confidenceCounts.MEDIUM,
                            low: summary.confidenceCounts.LOW,
                            error: summary.confidenceCounts.ERROR,
                          })}
                        </TableCell>
                        <TableCell align="right">
                          <Button size="small" onClick={() => dismissHistory(summary.id)}>
                            {t("externalRegister.history.buttons.dismiss")}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}

export default ExternalRegisterImportHistoryPage;
